if (!customElements.get('stud-cart-stack')) {
    customElements.define('stud-cart-stack', class extends HTMLElement {
      connectedCallback() {
        this.events = new AbortController();
        this.version = 0;
        this.renderCount(Number(this.dataset.count) || 0);

        const schedule = event => this.onCartChange(event);
        const options = { signal: this.events.signal };

        [
          'cart:updated',
          'cart:refresh',
          'variant:added',
          'cart:change',
          'cart:changed'
        ].forEach(name => {
          document.addEventListener(name, schedule, options);
        });

        window.addEventListener('pageshow', schedule, options);
        window.addEventListener('focus', schedule, options);

        if ('PerformanceObserver' in window) {
          try {
            this.observer = new PerformanceObserver(list => {
              const cartChanged = list.getEntries().some(entry => {
                const url = new URL(entry.name, location.origin);

                return (
                  url.origin === location.origin &&
                  /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(url.pathname)
                );
              });

              if (cartChanged) this.scheduleRefresh();
            });

            this.observer.observe({
              type: 'resource',
              buffered: false
            });
          } catch (error) {}
        }
      }

      disconnectedCallback() {
        this.events?.abort();
        this.request?.abort();
        this.observer?.disconnect();
        clearTimeout(this.timer);
      }

      renderNeilmedCount(value) {
        const count = Math.max(0, Math.floor(Number(value) || 0));
        const rates = [Number(this.getAttribute('data-discount-2')), Number(this.getAttribute('data-discount-3')), Number(this.getAttribute('data-discount-4'))];
        const progress = count < 2 ? count * 100 / 6 : Math.min(100, (count - 1) * 100 / 3);
        let message;
        if (count >= 4) message = this.dataset.messageMax.replace('__DISCOUNT__', rates[2]);
        else if (count >= 2) message = this.dataset.messageNext.replace('__CURRENT__', rates[count - 2]).replace('__DISCOUNT__', rates[count - 1]);
        else if (count === 1) message = this.dataset.messageOne.replace('__DISCOUNT__', rates[0]);
        else message = this.dataset.messageBefore.replace('__COUNT__', 2).replace('__DISCOUNT__', rates[0]);
        this.dataset.count = String(count);
        this.style.setProperty('--stack-progress', progress + '%');
        this.querySelector('[data-stack-message]').textContent = message;
        const track = this.querySelector('[data-stack-track]');
        track.setAttribute('aria-valuenow', String(Math.min(count, 4)));
        track.setAttribute('aria-valuetext', this.dataset.messageCount.replace('__COUNT__', count));
        this.querySelectorAll('[data-stack-tier]').forEach(tier => {
          tier.classList.toggle('is-reached', count >= Number(tier.dataset.stackTier));
        });
      }

      renderCount(value) {
        if (this.dataset.mode === 'neilmed') return this.renderNeilmedCount(value);
        const count = Math.max(0, Math.floor(Number(value) || 0));

        const progress = count < 3
          ? count * 100 / 9
          : Math.min(100, (count - 2) * 100 / 3);

        const message = count >= 5
          ? 'Maximale staffel bereikt: 10% korting.'
          : count === 4
            ? '8% bereikt · Nog 1 piercing tot 10% korting.'
            : count === 3
              ? '5% bereikt · Nog 1 piercing tot 8% korting.'
              : count === 2
                ? 'Voeg nog 1 piercing toe voor 5% korting.'
                : `Voeg nog ${3 - count} piercings toe voor 5% korting.`;

        this.dataset.count = String(count);

        this.style.setProperty(
          '--stack-progress',
          `${progress}%`
        );

        this.querySelector('[data-stack-message]').textContent = message;

        const track = this.querySelector('[data-stack-track]');

        track.setAttribute(
          'aria-valuenow',
          String(Math.min(count, 5))
        );

        track.setAttribute(
          'aria-valuetext',
          `${count} deelnemende piercings`
        );

        this.querySelectorAll('[data-stack-tier]').forEach(tier => {
          tier.classList.toggle(
            'is-reached',
            count >= Number(tier.dataset.stackTier)
          );
        });
      }

      onCartChange(event) {
        const sectionHtml = event?.detail?.cart?.sections?.[this.dataset.section];
        if (typeof sectionHtml === 'string') {
          const fresh = this.findFresh(sectionHtml);
          if (fresh) {
            clearTimeout(this.timer);
            this.version += 1;
            this.request?.abort();
            this.renderCount(Number(fresh.dataset.count));
            return;
          }
        }
        this.scheduleRefresh();
      }

      findFresh(markup) {
        const html = new DOMParser().parseFromString(markup, 'text/html');
        return Array.from(html.querySelectorAll('stud-cart-stack')).find(element => {
          return element.dataset.section === this.dataset.section && element.dataset.block === this.dataset.block;
        });
      }

      scheduleRefresh() {
        clearTimeout(this.timer);
        this.version += 1;
        this.request?.abort();

        this.timer = setTimeout(() => this.refresh(), 200);
      }

      async refresh() {
        if (!this.isConnected || !this.dataset.section) return;

        const version = this.version;
        const request = new AbortController();

        this.request = request;

        const timeout = setTimeout(() => request.abort(), 10000);

        try {
          const url = new URL(window.location.href);

          url.searchParams.delete('sections');
          url.searchParams.set('section_id', this.dataset.section);
          url.searchParams.set('_ss_stack', String(Date.now()));

          const response = await fetch(url, {
            credentials: 'same-origin',
            cache: 'no-store',
            signal: request.signal
          });

          if (!response.ok) {
            throw new Error('Stack unavailable');
          }

          const fresh = this.findFresh(await response.text());

          if (!fresh) {
            throw new Error('Stack unavailable');
          }

          if (!this.isConnected || version !== this.version) return;

          this.renderCount(Number(fresh.dataset.count));
        } catch (error) {
          if (this.isConnected && version === this.version) {
            this.renderCount(0);

            this.querySelector('[data-stack-message]').textContent =
              this.dataset.mode === 'neilmed' ? this.dataset.messageFallback : '3 piercings = 5% · 4 = 8% · 5 of meer = 10%';

            this.querySelector('[data-stack-track]').setAttribute(
              'aria-valuetext',
              'Actuele winkelmand kon niet worden opgehaald'
            );
          }
        } finally {
          clearTimeout(timeout);
        }
      }
    });
  }
